import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { requestReactivation, userLogin } from "../api/userApi";
import { useUser } from "../context/userContext";
import { getDefaultRoute } from "../utils/routeUtils";

export default function LoginForm() {
    const navigate = useNavigate();

    const { user, setUser } = useUser();

    // If already logged in, redirect to dashboard
    useEffect(() => {
        if (user?.userId && localStorage.getItem("Token")) {
            const roles = user.roles || [];
            navigate(getDefaultRoute(roles), { replace: true });
        }
    }, [user, navigate]);

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    // Set only when the account is deactivated. There is nothing the person can do
    // on this screen, so the message carries a way out — now a request that lands in
    // Approvals rather than a WhatsApp to one person's phone, so the ask is recorded
    // and any Super Admin can act on it.
    // (user 2026-10-29: "whatsapp replace entirely")
    const [isDeactivated, setIsDeactivated] = useState(false);
    const [reactivationSent, setReactivationSent] = useState(false);
    const [reactivationBusy, setReactivationBusy] = useState(false);
    const [loading, setLoading] = useState(false);

    const login = async () => {
        setError("");
        setLoading(true);
        try {
            const loginResponse = await userLogin(username, password);

            if (loginResponse.status === 200) {
                localStorage.setItem("UserId", loginResponse.data.userId);
                localStorage.setItem("Username", loginResponse.data.username);
                localStorage.setItem("FirstName", loginResponse.data.firstName || "");
                localStorage.setItem("LastName", loginResponse.data.lastName || "");
                localStorage.setItem("Roles", JSON.stringify(loginResponse.data.roles));
                localStorage.setItem("RoleRights", JSON.stringify(loginResponse.data.roleRights));
                localStorage.setItem("GroupName", loginResponse.data.groupName);
                localStorage.setItem("Token", loginResponse.data.token);
                localStorage.setItem("RefreshToken", loginResponse.data.refreshToken);
                localStorage.setItem("ProfilePhotoUrl", loginResponse.data.profilePhotoUrl || "");
                localStorage.setItem("MustAcceptAgreement", String(loginResponse.data.mustAcceptAgreement === true));
                localStorage.setItem("ApplicationStatus", loginResponse.data.applicationStatus || "Active");
                localStorage.setItem("AccessBlocked", String(loginResponse.data.accessBlocked === true));
                localStorage.setItem("CanCreateSale", String(loginResponse.data.canCreateSale === true));

                setUser({
                    userId: loginResponse.data.userId,
                    username: loginResponse.data.username,
                    firstName: loginResponse.data.firstName,
                    lastName: loginResponse.data.lastName,
                    roles: loginResponse.data.roles,
                    roleRights: loginResponse.data.roleRights,
                    groupName: loginResponse.data.groupName,
                    profilePhotoUrl: loginResponse.data.profilePhotoUrl,
                    mustAcceptAgreement: loginResponse.data.mustAcceptAgreement === true,
                    applicationStatus: loginResponse.data.applicationStatus || "Active",
                    accessBlocked: loginResponse.data.accessBlocked === true,
                    canCreateSale: loginResponse.data.canCreateSale === true,
                });

                const status = loginResponse.data.applicationStatus || "Active";
                if (status === "PendingReview" || status === "PendingApproval") {
                    navigate('/application-status');
                } else if (loginResponse.data.mustAcceptAgreement) {
                    navigate('/accept-agreement');
                } else {
                    navigate(getDefaultRoute(loginResponse.data.roles, loginResponse.data.canCreateSale === true));
                }
            }
            else {
                setError("Login failed. Please check your credentials.");
            }
        }
        catch (error) {
            console.error("Login error:", error);
            /* The API says WHICH of the three went wrong - no such username, wrong
               password, or deactivated - so the page can say it instead of sending
               somebody round in circles retyping a password. An older API, or a network
               failure with no response at all, still falls back to the generic line. */
            const data = error?.response?.data;
            if (data && typeof data === 'object' && data.message) {
                setError(data.message);
                setIsDeactivated(data.code === "deactivated");
            } else if (typeof data === 'string' && data.trim()) {
                setError(data);
                setIsDeactivated(false);
            } else {
                setError("Login failed. Please try again.");
                setIsDeactivated(false);
            }
        }
        finally {
            setLoading(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setError("");
        setIsDeactivated(false);
        login();
    };

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: '#F8FAFC',
            padding: '16px',
            boxSizing: 'border-box',
        }}>
            <div style={{
                maxWidth: 420,
                width: '100%',
                borderRadius: 14,
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                padding: '40px 32px',
                background: 'white',
                boxSizing: 'border-box',
            }}>
                <img src="/nexwaveLogo.png" alt="Nexwave" style={{ height: 64, display: 'block', margin: '0 auto 12px' }} />
                <div style={{ textAlign: 'center', fontFamily: 'Poppins', fontWeight: 700, fontSize: 24, color: '#0F172A', marginBottom: 4 }}>
                    nexwave
                </div>
                <div style={{ textAlign: 'center', fontSize: 14, color: '#1A8CFF', fontWeight: 600, fontFamily: 'Poppins', marginBottom: 32 }}>
                    Towards No.1
                </div>

                {error && (
                    <div style={{
                        background: '#FEF2F2',
                        border: '1px solid #FECACA',
                        borderRadius: 8,
                        padding: 12,
                        color: '#DC2626',
                        fontSize: 13,
                        marginBottom: 16,
                    }}>
                        {error}
                        {isDeactivated && (
                            reactivationSent ? (
                                <div style={{
                                    marginTop: 10, padding: '10px 12px', borderRadius: 8,
                                    background: '#ECFDF5', border: '1px solid #A7F3D0',
                                    color: '#047857', fontWeight: 600, fontSize: 13, textAlign: 'center',
                                }}>
                                    Request sent. An admin will review it — you&apos;ll be able to log in once it&apos;s approved.
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    disabled={reactivationBusy || !username.trim()}
                                    onClick={async () => {
                                        setReactivationBusy(true);
                                        try {
                                            await requestReactivation(username.trim(), '');
                                            setReactivationSent(true);
                                        } catch {
                                            // The endpoint answers the same way whatever happens, so the
                                            // only way here is the network. Say so rather than claim it sent.
                                            setReactivationSent(false);
                                            setError('Could not send the request. Check your connection and try again.');
                                        } finally {
                                            setReactivationBusy(false);
                                        }
                                    }}
                                    style={{
                                        display: 'block', width: '100%', marginTop: 10,
                                        padding: '10px 12px', borderRadius: 8, border: 'none',
                                        background: (reactivationBusy || !username.trim()) ? '#CBD5E1' : '#1A8CFF',
                                        color: '#fff', fontWeight: 700, fontSize: 13,
                                        textAlign: 'center', cursor: (reactivationBusy || !username.trim()) ? 'default' : 'pointer',
                                    }}
                                >
                                    {reactivationBusy ? 'Sending…' : 'Request account activation'}
                                </button>
                            )
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                        Username
                    </label>
                    <input
                        type="text"
                        placeholder="Enter username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: 8,
                            border: '1px solid #E2E8F0',
                            fontSize: 14,
                            fontFamily: 'Poppins',
                            marginBottom: 16,
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#1A8CFF'}
                        onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                    />

                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                        Password
                    </label>
                    <input
                        type="password"
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: 8,
                            border: '1px solid #E2E8F0',
                            fontSize: 14,
                            fontFamily: 'Poppins',
                            marginBottom: 24,
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#1A8CFF'}
                        onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                    />

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%',
                            padding: '12px',
                            background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 15,
                            fontWeight: 600,
                            cursor: loading ? 'default' : 'pointer',
                            fontFamily: 'Poppins',
                            opacity: loading ? 0.7 : 1,
                        }}
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>

                <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, fontFamily: 'Poppins' }}>
                    <a href="/forgot-password" style={{ color: '#1A8CFF', fontWeight: 600, textDecoration: 'none' }}>
                        Forgot password?
                    </a>
                </div>

                <div style={{ textAlign: 'center', marginTop: 32, fontSize: 12, color: '#94A3B8' }}>
                    nexwave &copy; {new Date().getFullYear()}
                </div>
            </div>
        </div>
    );
}
